"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Bell } from "lucide-react";
import posthog from "posthog-js";
import { Button } from "@/ui/components/ui/button";

interface BackInStockFormProps {
	productSlug: string;
	variantId?: string;
	/** UI locale + channel so the confirmation mail can be localized. */
	locale?: string;
	channel?: string;
	/**
	 * Where this remind-me form is rendered, so out-of-stock demand is attributable.
	 * Defaults to the PDP; the calculator buy-bridge passes "calculator_buy_bridge".
	 */
	source?: string;
}

export function BackInStockForm({
	productSlug,
	variantId,
	locale,
	channel,
	source = "pdp",
}: BackInStockFormProps) {
	const t = useTranslations("stock");
	const [email, setEmail] = useState("");
	const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(email.trim())) {
			setStatus("error");
			return;
		}

		setStatus("loading");

		try {
			const res = await fetch("/api/back-in-stock", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ email: email.trim(), productSlug, variantId, locale, channel }),
			});

			if (res.ok) {
				setStatus("success");
				// Micro-conversion: when a matched vial is sold out, the remind-me signup is the
				// ONLY conversion available, so capture it (with the surface) to size out-of-stock
				// demand and attribute it to the calculator buy-bridge vs the PDP.
				posthog.capture("back_in_stock_requested", {
					product_slug: productSlug,
					variant_id: variantId ?? null,
					channel: channel ?? null,
					source,
				});
			} else {
				setStatus("error");
			}
		} catch {
			setStatus("error");
		}
	};

	if (status === "success") {
		return (
			<div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-950">
				<div className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
					<Bell className="h-4 w-4" />
					{t("notifyMeSuccess")}
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-3">
			<p className="text-sm font-medium text-muted-foreground">{t("notifyMe")}</p>
			<form onSubmit={handleSubmit} className="flex gap-2">
				<input
					type="email"
					value={email}
					onChange={(e) => {
						setEmail(e.target.value);
						if (status === "error") setStatus("idle");
					}}
					placeholder={t("notifyMePlaceholder")}
					className="h-10 flex-1 rounded-md border border-border bg-background px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
					required
				/>
				<Button type="submit" size="sm" className="h-10 shrink-0" disabled={status === "loading"}>
					<Bell className="mr-1.5 h-4 w-4" />
					{t("notifyMeSubmit")}
				</Button>
			</form>
			{status === "error" && <p className="text-xs text-destructive">{t("notifyMeEmailRequired")}</p>}
		</div>
	);
}
